import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { log } from "./log";
import { api } from "./api";
import { useAuth } from "./auth";

const BrandCtx = createContext(null);

// The theme system is now entirely token-driven (see /app/frontend/src/index.css).
// We store only `theme_mode` per brand ("light" | "dark") and let CSS swap the
// full palette. Legacy per-hex fields are ignored — only the mode matters.
const DEFAULT_THEME = {
  theme_mode: "light",
  mode: "light",
  welcome_message: "Welcome to MMS HUB CENTER.",
};

const VALID_MODES = new Set(["light", "dark"]);

function normalizeMode(theme) {
  if (!theme || typeof theme !== "object") return "light";
  const raw = theme.theme_mode || theme.mode;
  return VALID_MODES.has(raw) ? raw : "light";
}

export function applyTheme(theme = {}) {
  const mode = normalizeMode(theme);
  // Toggle the entire palette by setting the html data attribute.
  // Every component reads its colours from CSS variables scoped to
  // `:root[data-theme="light|dark"]` in index.css.
  document.documentElement.setAttribute("data-theme", mode);
  document.body.dataset.mode = mode;
  // Persist locally so first paint doesn't flash the wrong theme on
  // subsequent visits (e.g. login screen before /brands loads).
  try { localStorage.setItem("mms_theme_mode", mode); } catch {}
}

export function resetTheme() {
  applyTheme(DEFAULT_THEME);
}

// Apply persisted theme immediately so first paint isn't wrong.
if (typeof window !== "undefined") {
  try {
    const cached = localStorage.getItem("mms_theme_mode");
    if (cached && VALID_MODES.has(cached)) {
      document.documentElement.setAttribute("data-theme", cached);
    } else {
      document.documentElement.setAttribute("data-theme", "light");
    }
  } catch {
    document.documentElement.setAttribute("data-theme", "light");
  }
}

export function BrandProvider({ children }) {
  const { user } = useAuth();
  const [brands, setBrands] = useState([]);
  const [activeBrand, setActiveBrand] = useState(null);
  const [ready, setReady] = useState(false);

  const load = useCallback(async () => {
    if (user === null) { setReady(false); return; }
    if (user === false) { setBrands([]); setActiveBrand(null); resetTheme(); setReady(true); return; }
    setReady(false);
    try {
      const { data } = await api.get("/brands");
      setBrands(data);
      const activeId = user.active_brand_id;
      const ab = data.find(b => b.id === activeId) || null;
      setActiveBrand(ab);
      if (ab?.theme) applyTheme(ab.theme);
      else resetTheme();
    } catch (err) {
      log.warn("[brand] Failed to load brand list", err);
    }
    setReady(true);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const select = async (brandId) => {
    const { data } = await api.post(`/brands/${brandId}/select`);
    const b = brands.find(x => x.id === brandId) || data.brand;
    setActiveBrand(b);
    if (b?.theme) applyTheme(b.theme);
    return b;
  };

  const refresh = load;

  const updateActiveTheme = (theme) => {
    if (activeBrand) {
      setActiveBrand({ ...activeBrand, theme });
      applyTheme(theme);
    }
  };

  // Convenience helper — flip the active brand between light/dark and persist.
  const setThemeMode = async (mode) => {
    if (!VALID_MODES.has(mode) || !activeBrand?.id) return;
    // Apply immediately for a snappy UX; the server call reconciles.
    applyTheme({ theme_mode: mode });
    try {
      const patch = { theme_mode: mode, mode };
      const { data } = await api.put(`/brands/${activeBrand.id}/theme`, patch);
      if (data) setActiveBrand({ ...activeBrand, theme: data.theme });
    } catch (err) {
      log.warn("[brand] theme save failed", err);
    }
  };

  return (
    <BrandCtx.Provider value={{ brands, activeBrand, ready, select, refresh, updateActiveTheme, setThemeMode, themeMode: normalizeMode(activeBrand?.theme) }}>
      {children}
    </BrandCtx.Provider>
  );
}

export function useBrand() {
  return useContext(BrandCtx);
}

export const BRAND_EDIT_ROLES = ["brand_owner", "brand_admin", "designer", "product_manager", "production_manager", "pattern_maker"];
export const BRAND_ADMIN_ROLES = ["brand_owner", "brand_admin"];

export function useBrandPermissions() {
  const { activeBrand } = useContext(BrandCtx) || {};
  const { user } = useAuth();
  const role = activeBrand?.my_role;
  const isSuper = !!user?.is_super_admin;
  return {
    role,
    isSuper,
    canView: !!role || isSuper,
    canEdit: isSuper || BRAND_EDIT_ROLES.includes(role),
    canAdmin: isSuper || BRAND_ADMIN_ROLES.includes(role),
  };
}

export { DEFAULT_THEME };
